import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  ProSidebar,
  Menu,
  MenuItem,
  SubMenu,
  SidebarFooter,
  SidebarHeader,
  SidebarContent,
} from "react-pro-sidebar";
import { StandardURL } from "../App";

import "react-pro-sidebar/dist/css/styles.css"; // Import the sidebar styles
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faBars, // Changed to faBars for opening sidebar on small screens
  faChevronRight,
  faChartBar,
  faFileInvoice,
  faPiggyBank,
  faBuilding,
  faCalendarCheck,
  faHome,
  faSignOutAlt,
  faComment,
  faWrench,
  faDoorOpen,
  faMoneyBillWave,
  faFolderOpen,
  faExchangeAlt,
  faReceipt,
  faHandHoldingUsd,
  faChartLine,
  faPenToSquare,
  faHouseUser,
  faFileSignature,
  faConciergeBell,
  faWater,
  faUserTie,
  faUsers,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import { AuthGuard, RoleGuard } from "../App"; // Assuming these are correctly exported from App.js
import MoneyStatistics from "./moneystatistics";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";

const PropertyPage = () => {
  const { selectedPropertyId } = useParams(); // Although you're using localStorage for PropertyId
  const navigate = useNavigate();
  const location = useLocation();
  const [property, setProperty] = useState(null);
  const [dailySales, setDailySales] = useState(null);
  const [weeklySales, setWeeklySales] = useState(null);
  const [monthlySales, setMonthlySales] = useState(null);
  const [annualSales, setAnnualSales] = useState(null);
  const token = localStorage.getItem("token");
  const PropertyId = localStorage.getItem("selectedPropertyId");
  // Initialize collapsed based on screen size for responsiveness
  const [collapsed, setCollapsed] = useState(window.innerWidth < 768); // Collapsed by default on small screens
  const [accountCategory, setAccountCategory] = useState(null); // New state for account category

  // Effect to handle window resize for sidebar collapse
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setCollapsed(true); // Collapse sidebar on small screens
      } else {
        setCollapsed(false); // Expand sidebar on larger screens
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!token || !PropertyId) {
      // Navigate to login or property selection if essential data is missing
      if (!token) toast.error("Authentication required. Please log in.");
      if (!PropertyId) toast.error("No property selected. Please select one.");
      navigate("/landlord/home"); // Or a more appropriate fallback
      return;
    }

    // Fetch property details
    axios
      .get(`${StandardURL}/iNyumba/admin/properties/records/${PropertyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        setProperty(response.data.response);
      })
      .catch((error) => {
        console.error("Error fetching property:", error);
        toast.error("Failed to load property details.");
      });

    // Fetch sales data
    const salesEndpoints = {
      daily: `${StandardURL}/iNyumba/user/salesdata/viewdaily/${PropertyId}`,
      weekly: `${StandardURL}/iNyumba/user/salesdata/viewweekly/${PropertyId}`,
      monthly: `${StandardURL}/iNyumba/user/salesdata/viewmonths/${PropertyId}`,
      annual: `${StandardURL}/iNyumba/user/salesdata/viewannual/${PropertyId}`,
    };

    const setSalesData = {
      daily: setDailySales,
      weekly: setWeeklySales,
      monthly: setMonthlySales,
      annual: setAnnualSales,
    };

    for (const key in salesEndpoints) {
      axios
        .get(salesEndpoints[key], { headers: { Authorization: `Bearer ${token}` } })
        .then((response) => {
          setSalesData[key](response.data.response);
        })
        .catch((error) => {
          console.error(`Error fetching ${key} sales:`, error);
        });
    }

    // Fetch KYC data
    axios
      .get(`${StandardURL}/iNyumba/propertykyc/fetchavailability/${PropertyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.data.responseCode !== 200) {
          const missingDocs = response.data.response;
          toast.error(
            <div>
              Missing KYC Documents: {missingDocs}
              <br />
              <Link
                to="/landlord/propertykyc"
                className="text-black underline font-bold"
              >
                Submit Here
              </Link>
            </div>,
            {
              position: "top-right",
              autoClose: false,
            }
          );
        } else {
          toast.success("All KYC documents are submitted!", {
            position: "top-right",
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching KYC data:", error);
        toast.error("Failed to check KYC documents. Please try again.");
      });

    // --- New: Fetch Account Category ---
    axios
      .get(`${StandardURL}/iNyumba/admin/mpesaintroductioncontroller/category/${PropertyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.data.success && response.data.responseCode === 200) {
          setAccountCategory(response.data.response);
        } else {
          console.error("Failed to fetch account category:", response.data.response);
          // Fallback or default if fetching category fails
          setAccountCategory(null); // Or 'personal' as a safe default
        }
      })
      .catch((error) => {
        console.error("Error fetching account category:", error);
        // Fallback or default on network/server error
        setAccountCategory(null); // Or 'personal'
      });
    // --- End New: Fetch Account Category ---

  }, [PropertyId, token, navigate]); // Added navigate to dependency array

  const handleNavigation = (path) => () => navigate(path);

  // Function to check if a menu item is active
  const isActive = (path) => location.pathname === path;

  return (
    <RoleGuard>
      <AuthGuard>
        <div className="flex min-h-screen bg-gray-100">
          {/* Sidebar */}
          <ProSidebar
            collapsed={collapsed}
            className={`min-h-screen h-screen flex-col shadow-lg transition-all duration-300 ${collapsed ? 'w-20' : 'w-64'}`} // Added width transition
            style={{
              backgroundColor: "#2c3e50", // Darker blue-gray
              borderRight: "1px solid #34495e",
            }}
          >
            <SidebarHeader>
              <div className="flex justify-between items-center p-4 py-6">
                <h2
                  className={`text-2xl font-extrabold text-white transition-opacity duration-300 ${
                    collapsed ? "opacity-0 hidden" : "opacity-100 block"
                  }`}
                >
                  Admin Dashboard
                </h2>
                {/* Toggle button for both collapsed and expanded states */}
                <button
                  onClick={() => setCollapsed(!collapsed)}
                  className="text-white hover:text-blue-300 transition-colors duration-200 focus:outline-none"
                  aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                >
                  {collapsed ? (
                    <FontAwesomeIcon icon={faChevronRight} size="lg" />
                  ) : (
                    <FontAwesomeIcon icon={faArrowLeft} size="lg" />
                  )}
                </button>
              </div>
            </SidebarHeader>
            <SidebarContent>
              <Menu iconShape="circle">
                <MenuItem
                  icon={<FontAwesomeIcon icon={faHome} />}
                  onClick={handleNavigation("/Landlord/home")}
                  className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                    isActive("/Landlord/home")
                      ? "bg-blue-600 text-white"
                      : "hover:bg-gray-700 text-gray-300 hover:text-white"
                  }`}
                >
                  Home
                </MenuItem>
                <SubMenu
                  title="Property"
                  icon={<FontAwesomeIcon icon={faBuilding} />}
                  className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                    location.pathname.startsWith("/landlord/property") ||
                    location.pathname.startsWith("/landlord/rentals") ||
                    location.pathname.startsWith("/landlord/amenities") ||
                    location.pathname.startsWith("/landlord/waterunits") ||
                    location.pathname.startsWith("/landlord/checktrashprice") ||
                    location.pathname.startsWith("/landlord/caretaker")
                      ? "bg-blue-700 text-white"
                      : "text-gray-300"
                  }`}
                >
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faPenToSquare} />}
                    onClick={handleNavigation("/landlord/updateproperty")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/updateproperty")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-600 text-gray-400 hover:text-white"
                    }`}
                  >
                    Update Property
                  </MenuItem>
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faHouseUser} />}
                    onClick={handleNavigation("/landlord/rentals")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/rentals")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-600 text-gray-400 hover:text-white"
                    }`}
                  >
                    Rentals
                  </MenuItem>
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faFileSignature} />}
                    onClick={handleNavigation("/landlord/propertykyc")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/propertykyc")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-600 text-gray-400 hover:text-white"
                    }`}
                  >
                    Property KYC
                  </MenuItem>
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faConciergeBell} />}
                    onClick={handleNavigation("/landlord/amenities")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/amenities")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-600 text-gray-400 hover:text-white"
                    }`}
                  >
                    Amenities
                  </MenuItem>
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faWater} />}
                    onClick={handleNavigation("/landlord/waterunits")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/waterunits")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-600 text-gray-400 hover:text-white"
                    }`}
                  >
                    Water Units
                  </MenuItem>
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faTrashAlt} />}
                    onClick={handleNavigation("/landlord/checktrashprice")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/checktrashprice")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-600 text-gray-400 hover:text-white"
                    }`}
                  >
                    Trash Collection Fee
                  </MenuItem>
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faUserTie} />}
                    onClick={handleNavigation("/landlord/caretaker")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/caretaker")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-600 text-gray-400 hover:text-white"
                    }`}
                  >
                    Caretaker
                  </MenuItem>
                </SubMenu>
                <MenuItem
                  icon={<FontAwesomeIcon icon={faCalendarCheck} />}
                  onClick={handleNavigation("/landlord/reservations")}
                  className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                    isActive("/landlord/reservations")
                      ? "bg-blue-600 text-white"
                      : "hover:bg-gray-700 text-gray-300 hover:text-white"
                  }`}
                >
                  Reservations
                </MenuItem>

                {/* Conditional Rendering for Pending Bills MenuItem */}
                {accountCategory === "common" && (
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faMoneyBillWave} />}
                    onClick={handleNavigation("/landlord/pendingpayment")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/pendingpayment")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-700 text-gray-300 hover:text-white"
                    }`}
                  >
                    Pending Bills
                  </MenuItem>
                )}
                {accountCategory === "personal" && (
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faMoneyBillWave} />}
                    onClick={handleNavigation("/landlord/offline")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/offline")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-700 text-gray-300 hover:text-white"
                    }`}
                  >
                    Offline Payments
                  </MenuItem>
                )}
                {/* End Conditional Rendering */}

                <SubMenu
                  title="Records"
                  icon={<FontAwesomeIcon icon={faFolderOpen} />}
                  className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                    location.pathname.startsWith("/landlord/transactionsview") ||
                    location.pathname.startsWith("/landlord/recordsview") ||
                    location.pathname.startsWith("/landlord/settlement") ||
                    location.pathname.startsWith("/landlord/payinsstatistics") ||
                    location.pathname.startsWith("/landlord/payoutsstatistics")
                      ? "bg-blue-700 text-white"
                      : "text-gray-300"
                  }`}
                >
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faExchangeAlt} />}
                    onClick={handleNavigation("/landlord/transactionsview")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/transactionsview")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-600 text-gray-400 hover:text-white"
                    }`}
                  >
                    Transactions View
                  </MenuItem>
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faReceipt} />}
                    onClick={handleNavigation("/landlord/recordsview")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/recordsview")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-600 text-gray-400 hover:text-white"
                    }`}
                  >
                    Rent Payment Records
                  </MenuItem>
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faHandHoldingUsd} />}
                    onClick={handleNavigation("/landlord/settlement")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/settlement")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-600 text-gray-400 hover:text-white"
                    }`}
                  >
                    Settlements View
                  </MenuItem>
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faChartLine} />}
                    onClick={handleNavigation("/landlord/payinsstatistics")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/payinsstatistics")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-600 text-gray-400 hover:text-white"
                    }`}
                  >
                    Payins Statistics
                  </MenuItem>

                  <MenuItem
                    icon={<FontAwesomeIcon icon={faChartLine} />}
                    onClick={handleNavigation("/landlord/payoutsstatistics")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/payoutsstatistics")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-600 text-gray-400 hover:text-white"
                    }`}
                  >
                    Payouts Statistics
                  </MenuItem>
                </SubMenu>
                <SubMenu
                  title="Financial"
                  icon={<FontAwesomeIcon icon={faChartBar} />}
                  className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                    location.pathname.startsWith("/landlord/account") ||
                    location.pathname.startsWith("/landlord/withdrawals") ||
                    location.pathname.startsWith("/landlord/deposits")
                      ? "bg-blue-700 text-white"
                      : "text-gray-300"
                  }`}
                >
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faFileInvoice} />}
                    onClick={handleNavigation("/landlord/account")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/account")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-600 text-gray-400 hover:text-white"
                    }`}
                  >
                    Accounts View
                  </MenuItem>
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faMoneyBillWave} />}
                    onClick={handleNavigation("/landlord/withdrawals")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/withdrawals")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-600 text-gray-400 hover:text-white"
                    }`}
                  >
                    Withdraw
                  </MenuItem>
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faPiggyBank} />}
                    onClick={handleNavigation("/landlord/deposits")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/deposits")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-600 text-gray-400 hover:text-white"
                    }`}
                  >
                    Deposits
                  </MenuItem>
                </SubMenu>
                <SubMenu
                  title="Tenant"
                  icon={<FontAwesomeIcon icon={faUsers} />}
                  className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                    location.pathname.startsWith("/landlord/comments") ||
                    location.pathname.startsWith("/landlord/maintenance") ||
                    location.pathname.startsWith("/landlord/vacating")
                      ? "bg-blue-700 text-white"
                      : "text-gray-300"
                  }`}
                >
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faComment} />}
                    onClick={handleNavigation("/landlord/comments")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/comments")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-600 text-gray-400 hover:text-white"
                    }`}
                  >
                    Comments
                  </MenuItem>
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faWrench} />}
                    onClick={handleNavigation("/landlord/maintenance")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/maintenance")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-600 text-gray-400 hover:text-white"
                    }`}
                  >
                    Maintenance
                  </MenuItem>
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faDoorOpen} />}
                    onClick={handleNavigation("/landlord/vacating")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/vacating")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-600 text-gray-400 hover:text-white"
                    }`}
                  >
                    Vacating
                  </MenuItem>
                </SubMenu>
                <MenuItem
                  icon={<FontAwesomeIcon icon={faComment} />}
                  onClick={handleNavigation("/landlord/sendsms")}
                  className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                    isActive("/landlord/sendsms")
                      ? "bg-blue-600 text-white"
                      : "hover:bg-gray-700 text-gray-300 hover:text-white"
                  }`}
                >
                  Send SMS
                </MenuItem>
                <SubMenu
                  title="Settings"
                  icon={<FontAwesomeIcon icon={faWrench} />}
                  className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                    location.pathname.startsWith("/landlord/mpesasetting") ||
                    location.pathname.startsWith("/landlord/smssetting")
                      ? "bg-blue-700 text-white"
                      : "text-gray-300"
                  }`}
                >
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faMoneyBillWave} />}
                    onClick={handleNavigation("/landlord/mpesasetting")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/mpesasetting")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-600 text-gray-400 hover:text-white"
                    }`}
                  >
                    M-Pesa Settings
                  </MenuItem>

                  <MenuItem
                    icon={<FontAwesomeIcon icon={faComment} />}
                    onClick={handleNavigation("/landlord/smssetting")}
                    className={`py-2 my-1 rounded-md transition-colors duration-200 mx-2 ${
                      isActive("/landlord/smssetting")
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-600 text-gray-400 hover:text-white"
                    }`}
                  >
                    SMS Settings
                  </MenuItem>
                </SubMenu>

                <MenuItem
                  icon={<FontAwesomeIcon icon={faSignOutAlt} />}
                  onClick={handleNavigation("/logout")}
                  className="py-2 my-1 rounded-md transition-colors duration-200 mx-2 bg-red-800 text-white hover:bg-red-700"
                >
                  Logout
                </MenuItem>
              </Menu>
            </SidebarContent>
            {/* Sidebar Footer */}
            <SidebarFooter style={{ borderTop: "1px solid #34495e" }}>
              <div className="text-white text-center p-4 text-xs opacity-75">
                <p>© 2024 Reivax Softwares</p>
                <p>All Rights Reserved</p>
              </div>
            </SidebarFooter>
          </ProSidebar>

          <div className="flex-1 p-6 overflow-y-auto">
            {/* Toggle button for small screens */}
            {window.innerWidth < 768 && (
              <button
                onClick={() => setCollapsed(!collapsed)}
                className="lg:hidden text-gray-800 bg-white p-2 rounded-md shadow-md mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label={collapsed ? "Open sidebar" : "Close sidebar"}
              >
                <FontAwesomeIcon icon={faBars} size="lg" />
              </button>
            )}

            {/* Go back button */}
            <button
              onClick={() => navigate(-1)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md mb-6 transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              Back
            </button>

            {/* Sales Data Flexboxes */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg rounded-lg text-center transform transition-all duration-300 hover:scale-105 hover:shadow-xl cursor-pointer">
                <h3 className="text-lg font-semibold mb-2">Today's Rent</h3>
                <p className="text-3xl font-extrabold">
                  {dailySales || dailySales === 0
                    ? `KES ${dailySales}`
                    : "Loading..."}
                </p>
              </div>

              <div className="p-6 bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg rounded-lg text-center transform transition-all duration-300 hover:scale-105 hover:shadow-xl cursor-pointer">
                <h3 className="text-lg font-semibold mb-2">Week's Rent</h3>
                <p className="text-3xl font-extrabold">
                  {weeklySales || weeklySales === 0
                    ? `KES ${weeklySales}`
                    : "Loading..."}
                </p>
              </div>

              <div className="p-6 bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-lg rounded-lg text-center transform transition-all duration-300 hover:scale-105 hover:shadow-xl cursor-pointer">
                <h3 className="text-lg font-semibold mb-2">Month's Rent</h3>
                <p className="text-3xl font-extrabold">
                  {monthlySales || monthlySales === 0
                    ? `KES ${monthlySales}`
                    : "Loading..."}
                </p>
              </div>

              <div className="p-6 bg-gradient-to-br from-red-500 to-red-600 text-white shadow-lg rounded-lg text-center transform transition-all duration-300 hover:scale-105 hover:shadow-xl cursor-pointer">
                <h3 className="text-lg font-semibold mb-2">Annual Rent</h3>
                <p className="text-3xl font-extrabold">
                  {annualSales || annualSales === 0
                    ? `KES ${annualSales}`
                    : "Loading..."}
                </p>
              </div>
            </div>

            {/* Money Statistics Component */}
            <div className="bg-white p-6 rounded-lg shadow-lg mb-8">
              <MoneyStatistics propertyId={PropertyId} />
            </div>

            {/* Property details */}
            {property && (
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h1 className="text-4xl font-extrabold text-gray-800 mb-6 border-b-2 pb-2">
                  {property.title}
                </h1>
                <div className="mb-6 flex flex-col md:flex-row items-center md:items-start">
                  {property.photo && (
                    <img
                      src={property.photo}
                      alt={property.title}
                      className="w-full md:w-1/2 lg:w-1/3 object-cover rounded-lg shadow-md mr-0 md:mr-6 mb-4 md:mb-0 transition-transform duration-300 hover:scale-105"
                      style={{ maxHeight: "300px" }}
                    />
                  )}
                  <div className="flex-1">
                    <p className="mb-3 text-gray-700 text-lg">
                      <strong>Location:</strong> {property.location}
                    </p>
                    <p className="mb-3 text-gray-700 text-lg">
                      <strong>County:</strong> {property.county}
                    </p>
                    <p className="mb-3 text-gray-700 text-lg">
                      <strong>Description:</strong>{" "}
                      {property.description || "No description provided."}
                    </p>
                    <p
                      className={`mb-3 text-lg font-semibold ${
                        property.approvalstatus === "Approved"
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      <strong>Approval Status:</strong>{" "}
                      {property.approvalstatus}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </AuthGuard>
    </RoleGuard>
  );
};

export default PropertyPage;